import api from 'api';
import { AppointmentProduct } from 'api/Serializers/AppointmentProducts';
import Button from 'components/button';
import Callout from 'components/callout';
import Controls from 'components/controls';
import Loading from 'components/loading';
import { BookingType, IS_SERVER, QueryParams, UserType } from 'config';
import AuthenticationModal from 'containers/authentication';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useQuery from 'hooks/useQuery';
import { RouteParams } from 'models/route';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  getCart,
  getCartAppointmentProduct,
  getCartBookingType,
  getCartCheckoutMethod,
  getCartMultiAvailabilityList,
  getCartMultiAvailabilityOrigin,
  getCartPaymentMethod,
  getCartSingleAvailability,
  getCartStatus,
  getIsAuthenticated,
  getUser,
} from 'state/selectors';
import { fetchAccountParticipants } from 'state/slice/account';
import {
  resetCart,
  setCartAppointmentProduct,
  setCartBookingType,
  setCartMultiAvailabilityOrigin,
  setCartSingleAvailability,
  setMultiAvailabilityList,
  trackCartChanges,
} from 'state/slice/cart';
import { APP_ROUTES } from 'utils/routing';

import { PriceTagline } from './components';
import CheckoutResultModal from './steps/checkoutResult';
import MultiDateTimeStep from './steps/multiDatetime';
import OriginDateTimeStep from './steps/originDatetime';
import SelectCartParticipants from './steps/participants';
import SelectPaymentMethodAndBookNow from './steps/paymentMethod';
import { BookingError, BookingStep, StepState } from './types';
import { getBookingStepState, getCanEditCart, getStepLink } from './utils';

const NotAuthenticated = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const appointmentProduct = useSelector(getCartAppointmentProduct);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const [showModal, setShowModal] = useState(false);

  if (isAuthenticated) {
    return null;
  }

  const handleAuthClose = (isAuthenticated: boolean) => {
    if (!isAuthenticated) {
      dispatch(trackCartChanges('Cart Authentication declined'));
      enqueueSnackbar({
        message: 'You must sign in or create an account to continue',
        variant: 'error',
      });
    } else {
      dispatch(trackCartChanges('Cart updated', 'Signed in'));
    }
    setShowModal(false);
  };

  return (
    <>
      <div className="max-w-md mx-auto">
        <div className="md:hidden">
          <PriceTagline />
        </div>
        <div className="p-4 mt-12 shadow-lg sm:p-6 bg-background rounded-2xl">
          <div className="mb-4 font-bold text-center text-md">
            Manage bookings and chat with your instructor, anytime!
          </div>
          <div className="mb-6 text-center">
            To book your{' '}
            {appointmentProduct.activity.appointmentNoun.toLowerCase()} and
            access all the great features of Propel, sign in or create an
            account.
          </div>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => setShowModal(true)}
          >
            Sign in or create your account
          </Button>
        </div>
      </div>
      <AuthenticationModal
        flow="BookingFlow"
        initialForm="login"
        defaultUserType={UserType.Client}
        onClose={handleAuthClose}
        isOpen={showModal}
      />
    </>
  );
};

const ErrorCallout = ({
  errorType,
  onReset,
}: {
  errorType: BookingError;
  onReset(): void;
}) => (
  <div className="space-y-8">
    <Callout className="!pb-0" type="error" title="Booking error">
      {ERROR_MESSAGES[errorType]}
      <Controls>
        <Button variant="outlined" onClick={onReset}>
          Back
        </Button>
      </Controls>
    </Callout>
  </div>
);

const ERROR_MESSAGES: Record<BookingError, string> = {
  invalidUserType: 'Only registered Propel clients can book appointments.',
  invalidBookingType: 'Batch booking of Proposals is not supported.',
  appointmentProductFailed: 'Error getting booking data.',
  availabilityFailed: 'Error getting booking data.',
  availabilityNotBookable: 'The selected time is no longer available.',
};

interface ChildBookingFlowProps {
  appointmentProduct: AppointmentProduct;
  onCartReset(): void;
}

const MultiBookingFlow = ({
  appointmentProduct,
  onCartReset,
}: ChildBookingFlowProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const BOOKING_TYPE = 'multi';
  const query = useQuery();
  const availabilityId = query.get(QueryParams.Availability);
  const originAvailability = useSelector(getCartMultiAvailabilityOrigin);
  const availabilityList = useSelector(getCartMultiAvailabilityList);
  const selectedPaymentMethod = useSelector(getCartPaymentMethod);
  const cartStatus = useSelector(getCartStatus);
  const [errorType, setErrorType] = useState<BookingError>();
  const [isLoading, setIsLoading] = useState(true);
  const routeMatch = useRouteMatch<RouteParams>();
  const step = routeMatch.params.step as BookingStep;

  useEffect(() => {
    const fetchAvailabilityOrigin = async (availId: string) => {
      try {
        const response = await api.availability.retrieve(availId);
        dispatch(setCartMultiAvailabilityOrigin(response.data));
      } catch (err) {
        setErrorType('availabilityFailed');
        setIsLoading(false);
      }
    };
    const fetchAvailabilityList = async (availId: string) => {
      try {
        const response = await api.availability.weekly(availId);
        dispatch(setMultiAvailabilityList(response.data));
      } catch (err) {
        resetToOriginStep();
      } finally {
        setIsLoading(false);
      }
    };
    if (availabilityId !== null && availabilityId !== originAvailability?.id) {
      fetchAvailabilityOrigin(availabilityId);
    } else if (originAvailability === undefined && step !== 'datetime') {
      history.push(
        APP_ROUTES.BOOK.nav(appointmentProduct.id, BOOKING_TYPE, 'datetime')
      );
      setIsLoading(false);
    } else if (
      availabilityList === undefined &&
      originAvailability !== undefined
    ) {
      fetchAvailabilityList(originAvailability.id);
    } else {
      setIsLoading(false);
    }
  }, [availabilityId, originAvailability]);

  const resetToOriginStep = () => {
    const params = new URLSearchParams(location.search);
    params.delete(QueryParams.Availability);
    params.delete(QueryParams.Id);
    dispatch(resetCart());
    history.push({
      pathname: APP_ROUTES.BOOK.nav(
        appointmentProduct.id,
        BOOKING_TYPE,
        'datetime'
      ),
      search: params.toString() ? `?${params.toString()}` : '',
    });
  };

  const getStepDisplayState = (target: BookingStep): StepState =>
    !getCanEditCart(cartStatus)
      ? 'complete'
      : getBookingStepState(step, target, BOOKING_TYPE);

  const handleHistoryPush = (nextStep: BookingStep) => {
    history.push(
      getStepLink(nextStep, BOOKING_TYPE, {
        appointmentProduct: appointmentProduct,
        selectedAvailability: originAvailability,
      })
    );
  };

  const handleResetError = () => {
    onCartReset();
    setErrorType(undefined);
  };

  if (isLoading) {
    return <Loading message="Loading..." />;
  } else if (errorType !== undefined) {
    return <ErrorCallout errorType={errorType} onReset={handleResetError} />;
  } else {
    return (
      <>
        <div className="space-y-8">
          <OriginDateTimeStep
            state={step === 'datetime' ? 'editing' : 'hidden'}
            onClickContinue={() => handleHistoryPush('series')}
            onClickEdit={null}
            bookingType={BOOKING_TYPE}
          />
          <MultiDateTimeStep
            state={getStepDisplayState('series')}
            onClickContinue={() => handleHistoryPush('participants')}
            onClickEdit={() =>
              handleHistoryPush(
                !isLoading && originAvailability === undefined
                  ? 'datetime'
                  : 'series'
              )
            }
            bookingType={BOOKING_TYPE}
          />
          <SelectCartParticipants
            state={getStepDisplayState('participants')}
            onClickContinue={() =>
              handleHistoryPush(selectedPaymentMethod ? 'checkout' : 'payment')
            }
            onClickEdit={() => handleHistoryPush('participants')}
            bookingType={BOOKING_TYPE}
          />
          <NotAuthenticated />
          <SelectPaymentMethodAndBookNow
            state={getStepDisplayState('payment')}
            onClickContinue={() => handleHistoryPush('checkout')}
            onClickEdit={() => handleHistoryPush('payment')}
            bookingType={BOOKING_TYPE}
          />
        </div>
      </>
    );
  }
};

const SingleBookingFlow = ({
  appointmentProduct,
  onCartReset,
}: ChildBookingFlowProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const query = useQuery();
  const routeMatch = useRouteMatch<RouteParams>();
  const selectedAvailability = useSelector(getCartSingleAvailability);
  const selectedPaymentMethod = useSelector(getCartPaymentMethod);
  const cartStatus = useSelector(getCartStatus);
  const checkoutMethod = useSelector(getCartCheckoutMethod);
  const [isLoading, setIsLoading] = useState(true);
  const [errorType, setErrorType] = useState<BookingError>();
  const step = routeMatch.params.step as BookingStep;
  const availabilityId = query.get(QueryParams.Availability);
  const BOOKING_TYPE = 'single';

  useEffect(() => {
    const fetchAvailability = async (availId: string) => {
      try {
        const response = await api.availability.retrieve(availId);
        const availability = response.data;
        dispatch(setCartSingleAvailability(availability, 'AVAILABILITY'));
        if (!availability.isBookable) {
          setErrorType('availabilityNotBookable');
        }
      } catch (err) {
        setErrorType('availabilityFailed');
      } finally {
        setIsLoading(false);
      }
    };
    if (
      availabilityId === null ||
      availabilityId === selectedAvailability?.id
    ) {
      setIsLoading(false);
    } else {
      fetchAvailability(availabilityId);
    }
  }, [availabilityId]);

  const getStepDisplayState = (target: BookingStep): StepState =>
    !getCanEditCart(cartStatus)
      ? 'complete'
      : getBookingStepState(step, target, BOOKING_TYPE);

  const handleHistoryPush = (nextStep: BookingStep) => {
    history.push(
      getStepLink(nextStep, BOOKING_TYPE, {
        appointmentProduct: appointmentProduct,
        selectedAvailability: selectedAvailability,
      })
    );
  };

  const handleDateTimeEdit = () => {
    if (checkoutMethod === 'PROPOSAL') {
      return null;
    }
    return () => handleHistoryPush('datetime');
  };

  const handleResetError = () => {
    onCartReset();
    setErrorType(undefined);
  };

  if (isLoading) {
    return <Loading message="Loading..." />;
  } else if (errorType !== undefined) {
    return <ErrorCallout errorType={errorType} onReset={handleResetError} />;
  } else {
    return (
      <>
        <div className="space-y-8">
          <OriginDateTimeStep
            state={getStepDisplayState('datetime')}
            onClickContinue={() => handleHistoryPush('participants')}
            onClickEdit={handleDateTimeEdit()}
            bookingType={BOOKING_TYPE}
          />
          <SelectCartParticipants
            state={getStepDisplayState('participants')}
            onClickContinue={() =>
              handleHistoryPush(selectedPaymentMethod ? 'checkout' : 'payment')
            }
            onClickEdit={() => handleHistoryPush('participants')}
            bookingType={BOOKING_TYPE}
          />
          <NotAuthenticated />
          <SelectPaymentMethodAndBookNow
            state={getStepDisplayState('payment')}
            onClickContinue={() => handleHistoryPush('checkout')}
            onClickEdit={() => handleHistoryPush('payment')}
            bookingType={BOOKING_TYPE}
          />
        </div>
      </>
    );
  }
};

const BookingFlow = ({
  appointmentProductId,
  bookingType,
}: {
  appointmentProductId: string;
  bookingType: BookingType;
}) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const query = useQuery();
  const routeMatch = useRouteMatch<RouteParams>();
  const user = useSelector(getUser);
  const cart = useSelector(getCart);
  const cartStatus = useSelector(getCartStatus);
  const checkoutMethod = useSelector(getCartCheckoutMethod);
  const appointmentProduct = useSelector(getCartAppointmentProduct);
  const cartBookingType = useSelector(getCartBookingType);
  const [errorType, setErrorType] = useState<BookingError>();
  const [isLoading, setIsLoading] = useState(true);
  const step = routeMatch.params.step as BookingStep;

  useEffect(() => {
    const fetchAppointmentProductData = async () => {
      try {
        const response = await api.appointmentProducts.retrieve(
          appointmentProductId
        );
        dispatch(setCartAppointmentProduct(response.data));
      } catch (err) {
        setErrorType('appointmentProductFailed');
      } finally {
        setIsLoading(false);
      }
    };
    if (appointmentProduct && appointmentProductId === appointmentProduct.id) {
      setIsLoading(false);
      return;
    }
    fetchAppointmentProductData();
  }, [appointmentProductId]);

  useEffect(() => {
    if (cartBookingType !== bookingType) {
      dispatch(setCartBookingType(bookingType));
    }
    if (checkoutMethod === 'PROPOSAL' && bookingType === 'multi') {
      setErrorType('invalidBookingType');
    }
  }, [bookingType]);

  useEffect(() => {
    if (user) {
      dispatch(fetchAccountParticipants);
      if (![UserType.Client, UserType.Admin].includes(user.type)) {
        setErrorType('invalidUserType');
      }
    }
  }, [user]);

  useEffect(() => {
    if (cart?.status !== 'COMPLETE') {
      return;
    }
    const paramsObj: { id: string; last?: string } = { id: cart.id };
    if (query.get(QueryParams.Last)) {
      paramsObj.last = query.get(QueryParams.Last);
    }
    const params = new URLSearchParams(paramsObj);
    history.replace(
      APP_ROUTES.BOOK.nav(appointmentProduct.id, bookingType, 'confirmation') +
        '?' +
        params.toString()
    );
  }, [cart]);

  const handleCartReset = () => {
    setIsLoading(true);
    const params = new URLSearchParams(location.search);
    params.delete(QueryParams.Availability);
    params.delete(QueryParams.Id);
    dispatch(resetCart());
    history.push({
      pathname: APP_ROUTES.BOOK.nav(
        appointmentProduct.id,
        bookingType,
        'datetime'
      ),
      search: params.toString() ? `?${params.toString()}` : '',
    });
    setErrorType(undefined);
    setIsLoading(false);
  };

  const handleResetError = () => {
    handleCartReset();
    setErrorType(undefined);
  };

  if (IS_SERVER || isLoading || bookingType !== cartBookingType) {
    return <Loading message="Loading..." />;
  } else if (errorType !== undefined) {
    return <ErrorCallout errorType={errorType} onReset={handleResetError} />;
  } else if (!step) {
    history.replace(
      getStepLink('datetime', bookingType, {
        appointmentProduct,
      })
    );
    return <Loading />;
  } else {
    return (
      <>
        {bookingType === 'multi' ? (
          <MultiBookingFlow
            appointmentProduct={appointmentProduct}
            onCartReset={handleCartReset}
          />
        ) : (
          <SingleBookingFlow
            appointmentProduct={appointmentProduct}
            onCartReset={handleCartReset}
          />
        )}
        <CheckoutResultModal onCartReset={handleCartReset} />
        {cartStatus === 'PROCESSING' && (
          <Loading
            message={`Confirming your ${'booking'.pluralize(
              bookingType === 'multi'
            )}...`}
          />
        )}
      </>
    );
  }
};

export default BookingFlow;
